/**
 * 发票申请记录
 * - 申请 POST /home/invoice-record/apply
 * - 列表 GET /home/invoice-record/list
 * - 详情 GET /home/invoice-record/detail?record_id=
 */

import { http } from "@/http/axios";
import type { OrderProductType } from "@/api/order";

/** 列表单条（与 Apipost 返回字段一致；其余为可选扩展字段） */
export interface InvoiceRecordItem {
  record_id: number;
  record_no: string;
  order_type: number;
  order_type_text: string;
  head_name: string;
  type: number;
  type_text: string;
  invoice_type: number;
  invoice_type_text: string;
  invoice_price: string;
  invoice_url: string;
  invoice_code: string;
  invoice_no: string;
  check_code: string;
  bank: string;
  bank_no: string;
  address: string;
  status: number;
  status_text: string;
  reason: string;
  add_time: string;
  /** 部分环境可能仍返回 */
  user_id?: number;
  user_name?: string;
  user_phone?: string;
  user_email?: string;
  order_no?: string;
  product_name?: string;
  duty_number?: string;
  update_time?: string | null;
}

export interface InvoiceRecordListPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface InvoiceRecordListResponse {
  code: number;
  message: string;
  data: {
    list: InvoiceRecordItem[];
    pagination: InvoiceRecordListPagination;
  };
}

export interface InvoiceRecordListParams {
  page?: number;
  limit?: number;
}

/** 开票申请 POST /home/invoice-record/apply 请求体 */
export interface InvoiceApplyParams {
  order_id: number;
  /** 订单业务类型（与后端约定：1酒店 2线路 3租车 4导游） */
  order_type: number;
  /** 发票抬头（个人姓名或企业名称） */
  head_name: string;
  /** 抬头类型：1个人 2企业 */
  type: number;
  /** 发票种类：1普通发票2专用发票 */
  invoice_type: number;
  user_email: string;
  /** 企业税号；个人可为空串 */
  duty_number: string;
  phone: string;
  /** 开户银行 */
  bank: string;
  /** 银行账号 */
  bank_no: string;
  /** 注册地址 */
  address: string;
}

export interface InvoiceApplyData {
  record_id: number;
  record_no: string;
  status: number;
  status_text: string;
}

export interface InvoiceApplyResponse {
  code: number;
  message: string;
  data: InvoiceApplyData;
}

/**
 * 将前端订单品类映射为开票接口 order_type
 * 若与后端枚举不一致，仅改此处即可
 */
export function mapProductTypeToInvoiceOrderType(
  productType: OrderProductType | string | undefined, 
): number {
  switch (productType) {
    case "hotel":
      return 1;
    case "tour":
      return 2;
    case "car":
      return 3;
    case "guide":
      return 4;
    default:
      return 1;
  }
}

/** 用户申请开票 POST /home/invoice-record/apply */
export async function applyInvoiceRecord(
  params: InvoiceApplyParams,
): Promise<InvoiceApplyData> {
  const res = await http.post<InvoiceApplyResponse>(
    "/home/invoice-record/apply",
    params,
  );
  const body = res.data;
  if (body.code !== 200) {
    throw new Error(body.message || "发票申请失败");
  }
  return body.data;
}

/** 详情接口返回的关联订单（酒店等） */
export interface InvoiceOrderInfo {
  order_no: string;
  hotel_name: string;
  room_name: string;
  check_in_date: string;
  check_out_date: string;
  total_amount: string;
}

export interface InvoiceRecordDetailResponse {
  code: number;
  message: string;
  data: {
    record: InvoiceRecordItem;
    order_info: InvoiceOrderInfo | null;
  };
}

/** 发票申请记录详情 GET /home/invoice-record/detail?record_id= */
export async function fetchInvoiceRecordDetail(
  recordId: number | string,
): Promise<InvoiceRecordDetailResponse["data"]> {
  const res = await http.get<InvoiceRecordDetailResponse>(
    "/home/invoice-record/detail",
    { params: { record_id: String(recordId) } },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取发票详情失败");
  }

  return res.data.data;
}

/** 发票申请记录列表 */
export async function fetchInvoiceRecordList(
  params?: InvoiceRecordListParams,
): Promise<InvoiceRecordListResponse["data"]> {
  const res = await http.get<InvoiceRecordListResponse>(
    "/home/invoice-record/list",
    { params },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取发票列表失败");
  }

  return res.data.data;
}
